import { useEffect, useState } from "react";

import { useFormik } from "formik";
import dayjs from "dayjs";

import Col from "react-bootstrap/Col";

import { useLayout } from "@/../_metronic/layout/core";
import { useCurrentAccount } from "@/hooks/useCurrentAccount";
import { useDebounce } from "@/hooks/useDebounce";
import { exportExcel } from "@/utils/exports";
import { formatIdentifier } from "@/utils/formatIdentifier";
import { dischargeFilterSchema } from "@/utils/Schemas/dischargeFilterSchema";
import { getDischargeSummary, PaginatedDischarge, DischargeSummary } from "@/services/sintegradaserver/discharge/getDischargeSummary";
import { dischargeFilterInitialValues, TypeofDischargeFilterInitialValues } from "@/utils/initialValues/dischargeFilter";
import { DischargeType, dischargeTypeMessages } from "@/utils/enums/sintegrada/discharge/DischargeType";
import { Gender, genderMessages } from "@/utils/enums/sintegrada/Gender";

import { ReportBaseLayout } from "@/components/sintegrada/ReportBaseLayout";
import { LoadingScreen } from "@/components/LoadingScreen";
import { SearchInput } from "@/components/SearchInput";
import { DischargeReportFilter } from "./DischargeReportFilter";
import { DischargeIndividualTable } from "./DischargeIndividualTable";
import { DischargeResumeTable } from "./DischargeResumeTable";
import { PaginationLinks } from "@/components/PaginationLinks";

export function DischargeReport() {
	const { setTitle } = useLayout();
	const { cnes, ibgeCode } = useCurrentAccount();

	const [page, setPage] = useState(1);

	const [isLoading, setIsLoading] = useState(false);
	const [isLoadingExportExcel, setIsLoadingExportExcel] = useState(false);

	const [discharges, setDischarges] = useState<PaginatedDischarge>({} as PaginatedDischarge);
	const [dischargesSummary, setDischargesSummary] = useState<DischargeSummary>({} as DischargeSummary);

	const [search, setSearch] = useState("");
	const debouncedSearch = useDebounce(search, 2000);

	const dischargeFilterValues = getSessionStorageDischargeFilterValues();
	const hasDischargeFilterValues = Boolean(Object.keys(dischargeFilterValues).length);

	const hasDischarges = Boolean(discharges?.data?.length);
	const hasDischargeSummary = Boolean(dischargesSummary?.data?.length);

	const disabledExportExcelIndividualTableButton = dischargeFilterValues.individual && !hasDischarges;
	const disabledExportExcelResumeTableButton = !dischargeFilterValues.individual && !hasDischargeSummary;

	const formik = useFormik({
		initialValues: dischargeFilterInitialValues,
		validationSchema: dischargeFilterSchema,
		async onSubmit(values) {
			const transformedParams = transformParams(values);

			const data = await getDischargeSummary(transformedParams);

			if (values.individual) {
				setDischarges(data as PaginatedDischarge);

				sessionStorage.setItem("dischargeFilterValues", JSON.stringify(values));
			
				return;
			}

			setDischargesSummary(data as DischargeSummary);

			sessionStorage.setItem("dischargeFilterValues", JSON.stringify(values));
		},
	});

	function transformParams(values: TypeofDischargeFilterInitialValues) {
		const GROUP_BY_INDIVIDUAL = "DETAILED";
		const GROUP_BY_TOTAL = "TOTAL";
		const group_by = values.individual ? GROUP_BY_INDIVIDUAL : GROUP_BY_TOTAL;

		const minAge = values.min_age ? Number(values.min_age) : null;
		const maxAge = values.max_age ? Number(values.max_age) : null;

		return {
			cnes,
			ibgeCode,
			group_by,
			discharge_type: values.dischargeType?.value,
			discharge_start_date: values.startDate,
			discharge_end_date: values.endDate,
			department: values.department?.value?.department,
			specialty_id: values.department?.value?.specialtyId,
			min_age: minAge,
			max_age: maxAge,
			gender: values.gender?.value,
			transfer_unit_id: values.transferUnit?.value,
		};
	}

	function getSessionStorageDischargeFilterValues() {
		const diagnosticHypothesisFilterData =
			sessionStorage.getItem("dischargeFilterValues") || "{}";
		const diagnosticHypothesisFilterDataJSON = JSON.parse(diagnosticHypothesisFilterData);

		return diagnosticHypothesisFilterDataJSON as TypeofDischargeFilterInitialValues;
	}

	async function handleGetDischargeSummary(page = 1, search = "") {
		setIsLoading(true);

		const transformedParams = transformParams(dischargeFilterValues);

		const data = await getDischargeSummary({
			page,
			search,
			...transformedParams,
		});

		if (dischargeFilterValues.individual) {
			setDischarges(
				data as PaginatedDischarge
			);

			setIsLoading(false);

			return;
		}

		setDischargesSummary(data as DischargeSummary);

		setIsLoading(false);
	}

	async function handleChangePage(page: number) {
		setPage(page);
	}

	function transformDischargeResumeTableToExportExcel(dischargeSummary: DischargeSummary) {
		return dischargeSummary.data.map((item) => {
			const dischargeType = dischargeTypeMessages[item.discharge_type as DischargeType];

			return {
				"Tipo de alta": dischargeType,
				"Total": item.total,
			};
		});
	}

	function transformDischargeIndividualTableToExportExcel(discharges: PaginatedDischarge) {
		return discharges.data.map((item) => {
			const { patient } = item;

			const formattedIdentifier = formatIdentifier(patient.identifier);
			const formattedBirthdate = dayjs(patient.birthday).format("DD/MM/YYYY");
			const formattedGender = genderMessages[patient.sex as Gender] || "N/A";
			const formattedDischargeDate = dayjs(item.discharge_date).format("DD/MM/YYYY");

			return {
				"Nome do paciente": patient.name,
				"CPF": formattedIdentifier,
				"Dt. nasc.": formattedBirthdate,
				"Sexo": formattedGender,
				"Classificação": item.risk.name || "N/A",
				"Tipo de alta": item.discharge_type,
				"Unidade de transferência": item.transfer_unit_name || "N/A",
				"Data de alta": formattedDischargeDate,
				"Responsável": item.professional_name,
			};
		});
	}

	async function handleExportExcel() {
		setIsLoadingExportExcel(true);

		const params = transformParams(dischargeFilterValues);

		const data = await getDischargeSummary({
			...params,
			all_results: 1,
		});

		if (!dischargeFilterValues.individual) {
			const transformedData =
				transformDischargeResumeTableToExportExcel(data as DischargeSummary);

			exportExcel(
				transformedData,
				"Relatório de alta"
			);

			setIsLoadingExportExcel(false);

			return;
		}

		const transformedData = transformDischargeIndividualTableToExportExcel(
			data as PaginatedDischarge
		);

		exportExcel(transformedData, "Relatório de alta individual");

		setIsLoadingExportExcel(false);
	}

	useEffect(() => {
		if (hasDischarges || hasDischargeSummary) {
			handleGetDischargeSummary(page);
		}

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [page]);

	useEffect(() => {
		if (hasDischarges || hasDischargeSummary) {
			handleGetDischargeSummary(1, search);
		}

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [debouncedSearch]);

	useEffect(() => {
		setTitle("Relatório de alta");
		sessionStorage.removeItem("dischargeFilterValues");

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	return (
		<ReportBaseLayout>
			<LoadingScreen loading={formik.isSubmitting || isLoadingExportExcel} />

			<DischargeReportFilter
				formik={formik}
				handleExportExcel={handleExportExcel}
				disabledExportButton={disabledExportExcelIndividualTableButton || disabledExportExcelResumeTableButton}
			/>

			{hasDischargeFilterValues && (dischargeFilterValues.individual ? (
				<>
					<Col lg={12} className="d-flex justify-content-end mb-8">
						<SearchInput
							width="20rem"
							placeholder="Buscar por nome ou dt. nasc"
							value={search}
							setValue={setSearch}
						/>
					</Col>

					<DischargeIndividualTable
						data={discharges.data}
						isLoading={isLoading}
					/>

					{hasDischarges && (
						<PaginationLinks
							itemsPerPage={discharges.meta.per_page}
							pageNumber={discharges.meta.current_page}
							totalPages={discharges.meta.total}
							changeSelectedPage={handleChangePage}
						/>
					)}
				</>
			) : (
				<DischargeResumeTable
					data={dischargesSummary.data}
					isLoading={isLoading}
				/>
			))}
		</ReportBaseLayout>
	);
}
